import { useCallback, useEffect, useRef, useState } from 'react';

import DigitacaoService from '../services/Digitacao';
import { descreverErroDetalhado } from '../services/API';
import { MODOS_SESSAO } from '../components/Digitacao/PainelSessaoDigitacao';
import DigitacaoReservaStorage, { PREFETCH_RESERVA } from '../storage/DigitacaoReservaStorage';
import DigitacaoSalvamentoStorage from '../storage/DigitacaoSalvamentoStorage';

export const ORIGEM_FILA = 'FILA';

const MENSAGEM_ERRO_PADRAO = 'Não foi possível concluir a requisição.';

const ehFalhaRetentavel = error => {
    const status = error && error.response ? error.response.status : null;

    if (!status) return true;
    if (status === 408 || status === 429) return true;
    if (status >= 400 && status < 500) return false;

    return true;
};

export const useSessaoDigitacao = ({ mode = MODOS_SESSAO.DIGITAR, regiao = null, onRegistrado, onErro, onFilaVazia, onSalvamentoPendente, onSalvamentoConcluido } = {}) => {
    const [prova, setProva] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [enviando, setEnviando] = useState(false);
    const [filaVazia, setFilaVazia] = useState(false);
    const [erro, setErro] = useState(null);
    const [reservaLocal, setReservaLocal] = useState(() => DigitacaoReservaStorage.quantidade(mode));
    const [digitadasNaSessao, setDigitadasNaSessao] = useState(0);
    const [enviosPendentes, setEnviosPendentes] = useState(() => DigitacaoSalvamentoStorage.quantidade());

    const callbacksRef = useRef({ onRegistrado, onErro, onFilaVazia, onSalvamentoPendente, onSalvamentoConcluido });
    const carregouRef = useRef(false);
    const reabastecendoRef = useRef(false);
    const flushEmAndamentoRef = useRef(false);
    const provaAtualRef = useRef(null);

    useEffect(() => {
        callbacksRef.current = { onRegistrado, onErro, onFilaVazia, onSalvamentoPendente, onSalvamentoConcluido };
    }, [onRegistrado, onErro, onFilaVazia, onSalvamentoPendente, onSalvamentoConcluido]);

    const notificarErro = useCallback(error => {
        const descricao = descreverErroDetalhado(error, MENSAGEM_ERRO_PADRAO);

        if (callbacksRef.current.onErro) {
            callbacksRef.current.onErro(descricao.message, descricao.type);
        }

        return descricao.message;
    }, []);

    const requisitarProvaBackend = useCallback(async () => {
        const resposta = mode === MODOS_SESSAO.REVISAR
            ? await DigitacaoService.proximaRevisao()
            : await DigitacaoService.proxima(regiao);

        if (resposta.status === 204 || !resposta.data) return null;

        return resposta.data;
    }, [mode, regiao]);

    const sincronizarReservaLocal = useCallback(() => {
        setReservaLocal(DigitacaoReservaStorage.quantidade(mode));
    }, [mode]);

    const sincronizarEnviosPendentes = useCallback(() => {
        setEnviosPendentes(DigitacaoSalvamentoStorage.quantidade());
    }, []);

    const idsComSubmitPendente = useCallback(() => {
        return new Set(
            DigitacaoSalvamentoStorage.listar().map(item => item.payload && item.payload.idProva)
        );
    }, []);

    const completarComReservas = useCallback(async () => {
        try {
            const faltam = PREFETCH_RESERVA - DigitacaoReservaStorage.quantidade(mode);

            if (faltam <= 0) return;

            const resposta = mode === MODOS_SESSAO.REVISAR
                ? await DigitacaoService.reservasRevisao()
                : await DigitacaoService.reservas();

            const provas = resposta && Array.isArray(resposta.data) ? resposta.data : [];

            if (!provas.length) return;

            const exibida = provaAtualRef.current ? provaAtualRef.current.id : null;
            const pendentes = idsComSubmitPendente();
            const jaNaReserva = new Set(DigitacaoReservaStorage.ids(mode));

            const aproveitaveis = provas
                .filter(prova => prova && prova.id)
                .filter(prova => prova.id !== exibida && !pendentes.has(prova.id) && !jaNaReserva.has(prova.id))
                .slice(0, faltam);

            if (aproveitaveis.length) DigitacaoReservaStorage.adicionarVarias(mode, aproveitaveis);
        } catch (error) {
            // Falha ao recuperar reservas não bloqueia a sessão; segue para a fila.
            return;
        }
    }, [mode, idsComSubmitPendente]);

    const reabastecer = useCallback(async () => {
        if (reabastecendoRef.current) return;

        reabastecendoRef.current = true;

        try {
            await completarComReservas();

            for (let tentativa = 0; tentativa < PREFETCH_RESERVA; tentativa++) {
                if (DigitacaoReservaStorage.quantidade(mode) >= PREFETCH_RESERVA) break;

                const novaProva = await requisitarProvaBackend();

                if (!novaProva) break;

                const antes = DigitacaoReservaStorage.quantidade(mode);

                DigitacaoReservaStorage.adicionar(mode, novaProva);

                if (DigitacaoReservaStorage.quantidade(mode) <= antes) break;
            }
        } catch (error) {
            return;
        } finally {
            reabastecendoRef.current = false;
            sincronizarReservaLocal();
        }
    }, [mode, requisitarProvaBackend, sincronizarReservaLocal, completarComReservas]);

    const flushSalvamento = useCallback(async () => {
        if (flushEmAndamentoRef.current) return;

        if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

        let pendentes = DigitacaoSalvamentoStorage.listar();

        if (!pendentes.length) return;

        flushEmAndamentoRef.current = true;

        let enviados = 0;

        try {
            while (pendentes.length) {
                const item = pendentes[0];

                try {
                    await DigitacaoService.registrar(item.payload);

                    DigitacaoSalvamentoStorage.remover(item.id);
                    enviados += 1;
                } catch (error) {
                    if (ehFalhaRetentavel(error)) {
                        DigitacaoSalvamentoStorage.incrementarTentativas(item.id);
                        break;
                    }

                    DigitacaoSalvamentoStorage.remover(item.id);
                    notificarErro(error);
                }

                pendentes = DigitacaoSalvamentoStorage.listar();
            }
        } finally {
            flushEmAndamentoRef.current = false;
            sincronizarEnviosPendentes();
        }

        if (enviados > 0 && callbacksRef.current.onSalvamentoConcluido) {
            callbacksRef.current.onSalvamentoConcluido(enviados);
        }
    }, [notificarErro, sincronizarEnviosPendentes]);

    const carregarProxima = useCallback(async () => {
        setCarregando(true);
        setFilaVazia(false);
        setErro(null);
        setProva(null);
        provaAtualRef.current = null;

        try {
            const local = DigitacaoReservaStorage.proxima(mode);

            if (local) {
                provaAtualRef.current = local;
                setProva(local);
            } else {
                const novaProva = await requisitarProvaBackend();

                if (!novaProva) {
                    setFilaVazia(true);
                    if (callbacksRef.current.onFilaVazia) callbacksRef.current.onFilaVazia();
                } else {
                    provaAtualRef.current = novaProva;
                    setProva(novaProva);
                }
            }

            reabastecer();
            flushSalvamento();
        } catch (error) {
            setErro(notificarErro(error));
        } finally {
            sincronizarReservaLocal();
            setCarregando(false);
        }
    }, [mode, notificarErro, requisitarProvaBackend, reabastecer, flushSalvamento, sincronizarReservaLocal]);

    useEffect(() => {
        if (carregouRef.current) return;

        carregouRef.current = true;

        (async () => {
            await completarComReservas();
            carregarProxima();
        })();
    }, [completarComReservas, carregarProxima]);

    const regiaoAnteriorRef = useRef(regiao);

    useEffect(() => {
        if (regiaoAnteriorRef.current === regiao) return;

        regiaoAnteriorRef.current = regiao;
        carregarProxima();
    }, [regiao, carregarProxima]);

    useEffect(() => {
        const aoReconectar = () => flushSalvamento();

        window.addEventListener('online', aoReconectar);

        return () => window.removeEventListener('online', aoReconectar);
    }, [flushSalvamento]);

    const registrar = useCallback(async respostas => {
        if (!prova || enviando) return;

        const payload = { idProva: prova.id, respostas, origem: ORIGEM_FILA };

        setEnviando(true);

        try {
            const resposta = await DigitacaoService.registrar(payload);

            setDigitadasNaSessao(quantidade => quantidade + 1);

            if (callbacksRef.current.onRegistrado) callbacksRef.current.onRegistrado(resposta.data);

            await carregarProxima();
        } catch (error) {
            if (ehFalhaRetentavel(error)) {
                DigitacaoSalvamentoStorage.adicionar(payload);

                setDigitadasNaSessao(quantidade => quantidade + 1);
                sincronizarEnviosPendentes();

                if (callbacksRef.current.onSalvamentoPendente) callbacksRef.current.onSalvamentoPendente();

                await carregarProxima();
            } else {
                notificarErro(error);
            }
        } finally {
            setEnviando(false);
        }
    }, [prova, enviando, carregarProxima, notificarErro, sincronizarEnviosPendentes]);

    const invalidar = useCallback(async ({ motivo, observacao }) => {
        if (!prova || enviando) return;

        setEnviando(true);

        try {
            await DigitacaoService.invalidar({
                idProva: prova.id,
                motivo,
                observacao,
            });

            await carregarProxima();
        } catch (error) {
            notificarErro(error);
        } finally {
            setEnviando(false);
        }
    }, [prova, enviando, carregarProxima, notificarErro]);

    const pular = useCallback(() => carregarProxima(), [carregarProxima]);

    return {
        prova,
        carregando,
        enviando,
        filaVazia,
        erro,
        reservaLocal,
        digitadasNaSessao,
        enviosPendentes,
        carregarProxima,
        registrar,
        invalidar,
        pular,
    };
};

export default useSessaoDigitacao;
